'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiAuthAccessErrorEvent } from '@/src/api/client';
import { setBearerToken } from '@/src/api/auth-client';
import { normalizeApiError } from '@/src/api/error-utils';
import { authControllerMe } from '@/src/api/generated/sdk.gen';
import type { ActiveTenantDto, UserSummaryDto } from '@/src/api/generated/types.gen';
import type { SessionRefreshResult } from '@/src/auth/session-refresh';
import { refreshStoredTenantSession } from '@/src/auth/session-refresh';
import { replaceWhenAuthNavigationReady } from '@/src/auth/auth-navigation-hold';
import { publishAuthTerminalEvent } from '@/src/auth/auth-lifecycle';
import { getBearerToken, clearAuthStorage, getIdentityToken } from '@/src/auth/token-storage';
import { logger } from '@/src/logging/logger';
import type { AccessContext } from './access-types';

export const sessionExpiredMessage = 'Your session expired. Please sign in again.';
export const missingTenantSessionMessage = 'No active tenant session.';
export const sessionTemporarilyUnavailableMessage =
  'We couldn’t reach the server. Please check your connection and try again.';
export const sessionThrottledMessage =
  'Too many sign-in attempts. Please wait a moment and try again.';
export const accessContextRetryMessage =
  'We couldn’t load your account permissions. Please refresh, or sign in again if this continues.';

type AccessLoadResult = {
  access: AccessContext | null;
  activeTenant: ActiveTenantDto | null;
  user: UserSummaryDto | null;
  error: string | null;
  preserveExisting?: boolean;
};

type ApiResponseWithErrorStatus = {
  error?: unknown;
  response?: Response;
};

export type AccessContextState = AccessLoadResult & {
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const AccessContextValue = createContext<AccessContextState | undefined>(undefined);

export function AccessContextProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: AccessContextState;
}) {
  return <AccessContextValue.Provider value={value}>{children}</AccessContextValue.Provider>;
}

function clearSessionAccess(publishTerminal = false) {
  clearAuthStorage();
  if (publishTerminal) publishAuthTerminalEvent('invalid_session');
  setBearerToken(null);

  return {
    access: null,
    activeTenant: null,
    user: null,
    error: sessionExpiredMessage,
  };
}

function missingTenantAccess(): AccessLoadResult {
  setBearerToken(null);

  return {
    access: null,
    activeTenant: null,
    user: null,
    error: missingTenantSessionMessage,
  };
}

function transientRecoveryAccess(
  status: 'temporarily-unavailable' | 'throttled',
): AccessLoadResult {
  return {
    access: null,
    activeTenant: null,
    user: null,
    error: status === 'throttled' ? sessionThrottledMessage : sessionTemporarilyUnavailableMessage,
    preserveExisting: true,
  };
}

/**
 * Apply a typed recovery outcome within the access loop. Returns true when the
 * loop should re-evaluate (a refreshed or tenant-unavailable session changes the
 * available tokens); false otherwise. Only `invalid-session` is a definitive
 * logout-worthy state — transient and throttled outcomes preserve the session.
 */
function isRecoverableRefreshStatus(
  status: SessionRefreshResult['status'],
): status is 'refreshed' | 'tenant-unavailable' {
  return status === 'refreshed' || status === 'tenant-unavailable';
}

async function recoverStoredTenantSession(): Promise<SessionRefreshResult> {
  try {
    const result = await refreshStoredTenantSession();
    if (!result || typeof result.status !== 'string') {
      return { status: 'invalid-session' };
    }
    return result;
  } catch {
    return { status: 'temporarily-unavailable' };
  }
}

function authMeStatus(response: ApiResponseWithErrorStatus): number | null {
  return response.response?.status ?? normalizeApiError(response.error).statusCode;
}

export function AccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<AccessContext | null>(null);
  const [activeTenant, setActiveTenant] = useState<ActiveTenantDto | null>(null);
  const [user, setUser] = useState<UserSummaryDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const recoveryInProgressRef = useRef(false);

  const loadAccess = useCallback(async (allowSessionRecovery = true): Promise<AccessLoadResult> => {
    let canRecoverSession = allowSessionRecovery;

    while (true) {
      const token = getBearerToken();

      if (!token) {
        const identityToken = getIdentityToken();

        if (!identityToken && canRecoverSession) {
          const result = await recoverStoredTenantSession();
          canRecoverSession = false;
          if (isRecoverableRefreshStatus(result.status)) {
            continue;
          }
          if (result.status === 'invalid-session') {
            return clearSessionAccess(true);
          }
          return transientRecoveryAccess(result.status);
        }

        return identityToken ? missingTenantAccess() : clearSessionAccess();
      }

      setBearerToken(token);

      try {
        const response = await authControllerMe();

        if (response.error) {
          if (authMeStatus(response) === 401) {
            if (canRecoverSession) {
              const result = await recoverStoredTenantSession();
              canRecoverSession = false;
              if (isRecoverableRefreshStatus(result.status)) {
                continue;
              }
              if (result.status === 'invalid-session') {
                return clearSessionAccess(true);
              }
              return transientRecoveryAccess(result.status);
            }

            return clearSessionAccess(true);
          }

          logger.error('Failed to load access context', response.error);
          return {
            access: null,
            activeTenant: null,
            user: null,
            error: accessContextRetryMessage,
            preserveExisting: true,
          };
        }

        if (!response.data?.access) {
          logger.error('Failed to load access context', 'Missing access payload');
          return {
            access: null,
            activeTenant: null,
            user: null,
            error: accessContextRetryMessage,
            preserveExisting: true,
          };
        }

        return {
          access: response.data.access,
          activeTenant: response.data.activeTenant ?? null,
          user: response.data.user,
          error: null,
        };
      } catch (err) {
        if (normalizeApiError(err).statusCode === 401) {
          if (canRecoverSession) {
            const result = await recoverStoredTenantSession();
            canRecoverSession = false;
            if (isRecoverableRefreshStatus(result.status)) {
              continue;
            }
            if (result.status === 'invalid-session') {
              return clearSessionAccess(true);
            }
            return transientRecoveryAccess(result.status);
          }

          return clearSessionAccess(true);
        }

        logger.error('Failed to load access context', err);
        return {
          access: null,
          activeTenant: null,
          user: null,
          error: accessContextRetryMessage,
          preserveExisting: true,
        };
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await loadAccess();
      if (!result.preserveExisting) {
        setAccess(result.access);
        setActiveTenant(result.activeTenant);
        setUser(result.user);
      }
      setError(result.error);
    } finally {
      setIsLoading(false);
    }
  }, [loadAccess]);

  useEffect(() => {
    let isCancelled = false;

    async function loadInitialAccess() {
      const result = await loadAccess();

      if (isCancelled) {
        return;
      }

      setAccess(result.access);
      setActiveTenant(result.activeTenant);
      setUser(result.user);
      setError(result.error);
      setIsLoading(false);
    }

    void loadInitialAccess();

    return () => {
      isCancelled = true;
    };
  }, [loadAccess]);

  useEffect(() => {
    async function handleAuthAccessError(event: Event) {
      const statusCode = event instanceof CustomEvent ? event.detail?.statusCode : null;

      // Ignore stale callers that still publish authorization failures here.
      // Only invalid authentication is eligible for stored-session recovery.
      if (statusCode !== 401) {
        return;
      }

      if (recoveryInProgressRef.current) {
        return;
      }

      recoveryInProgressRef.current = true;
      setIsLoading(true);

      try {
        const result = await recoverStoredTenantSession();

        if (result.status === 'invalid-session') {
          clearAuthStorage();
          setBearerToken(null);
          publishAuthTerminalEvent('invalid_session');
          setAccess(null);
          setActiveTenant(null);
          setUser(null);
          setError(sessionExpiredMessage);
          setIsLoading(false);
          replaceWhenAuthNavigationReady('/login?reason=session-expired');
          return;
        }

        // Transient and throttled recoveries preserve the current session and
        // surface a retryable message without redirecting to login.
        if (
          result.status === 'temporarily-unavailable' ||
          result.status === 'throttled'
        ) {
          setError(
            result.status === 'throttled'
              ? sessionThrottledMessage
              : sessionTemporarilyUnavailableMessage,
          );
          setIsLoading(false);
          return;
        }

        // refreshed or tenant-unavailable: reload the current access state.
        const accessResult = await loadAccess(false);

        if (!accessResult.preserveExisting) {
          setAccess(accessResult.access);
          setActiveTenant(accessResult.activeTenant);
          setUser(accessResult.user);
        }
        setError(accessResult.error);
        setIsLoading(false);

        if (accessResult.error === sessionExpiredMessage) {
          replaceWhenAuthNavigationReady('/login?reason=session-expired');
        }
      } finally {
        recoveryInProgressRef.current = false;
      }
    }

    window.addEventListener(apiAuthAccessErrorEvent, handleAuthAccessError);

    return () => {
      window.removeEventListener(apiAuthAccessErrorEvent, handleAuthAccessError);
    };
  }, [loadAccess]);

  const value = useMemo(
    () => ({ access, activeTenant, user, isLoading, error, refresh }),
    [access, activeTenant, user, error, isLoading, refresh],
  );

  return <AccessContextValue.Provider value={value}>{children}</AccessContextValue.Provider>;
}

export function useAccess() {
  const value = useContext(AccessContextValue);

  if (!value) {
    throw new Error('useAccess must be used within AccessProvider');
  }

  return value;
}
